import { Injectable, NgZone } from '@angular/core';

const FLOATING_CLASS       = 'form-actions-floating';
const LAUNCHER_CLASS       = 'form-actions-under-launcher';
const LAUNCHER_ZONE_HEIGHT = 70;
const STUCK_CLASS          = 'form-actions-stuck';

// Marks a floating form action row while it is stuck to the bottom of the window,
// and while it sits in the bottom strip the help launcher covers.
@Injectable({
  providedIn: 'root'
})
export class FormActionsStuckService {
  private _launcherZone : IntersectionObserver = null;
  private _observed     : Set<Element>         = new Set();
  private _stuck        : IntersectionObserver = null;

  constructor ( private zone : NgZone ) {
    if ( typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined' ) {
      return;
    }

    this.zone.runOutsideAngular(() => this._start());
  }

  private _belowRoot ( entry : IntersectionObserverEntry ) : boolean {
    return entry.intersectionRatio < 1
      && !!entry.rootBounds
      && entry.boundingClientRect.bottom > entry.rootBounds.bottom;
  }

  private _handleMutations ( mutations : Array<MutationRecord> ) : void {
    mutations.forEach(( mutation : MutationRecord ) => {
      const element = mutation.target as Element;

      if ( element.classList.contains(FLOATING_CLASS) ) {
        this._track(element);
      }
      else if ( this._observed.has(element) ) {
        this._untrack(element);
      }
    });

    this._observed.forEach(( element : Element ) => {
      if ( !document.body.contains(element) ) {
        this._untrack(element);
      }
    });
  }

  private _observer ( className : string, rootMargin : string ) : IntersectionObserver {
    return new IntersectionObserver(entries => {
      entries.forEach(( entry : IntersectionObserverEntry ) => {
        entry.target.classList.toggle(className, this._belowRoot(entry));
      });
    }, {
      rootMargin,
      threshold: [ 1 ]
    });
  }

  private _start () : void {
    this._launcherZone = this._observer(LAUNCHER_CLASS, `0px 0px -${ LAUNCHER_ZONE_HEIGHT }px 0px`);
    this._stuck        = this._observer(STUCK_CLASS, '0px');

    new MutationObserver(mutations => this._handleMutations(mutations)).observe(document.body, {
      attributeFilter: [ 'class' ],
      attributes     : true,
      subtree        : true
    });
  }

  private _track ( element : Element ) : void {
    if ( !this._observed.has(element) ) {
      this._observed.add(element);
      this._launcherZone.observe(element);
      this._stuck.observe(element);
    }
  }

  private _untrack ( element : Element ) : void {
    this._observed.delete(element);
    this._launcherZone.unobserve(element);
    this._stuck.unobserve(element);
    element.classList.remove(LAUNCHER_CLASS, STUCK_CLASS);
  }
}
